import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";

function getAuthConfig(getState) {
  const token = getState().loginToken?.response?.token;
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
}

function getBusinessId(getState) {
  return getState().loginToken?.response?.user?.business_id;
}

function normalizeMailDomainStatusPayload(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    registered: Boolean(raw.registered),
    mail_domain_verified: Boolean(raw.mail_domain_verified),
    mail_auth_domain: raw.mail_auth_domain ?? null,
    ticket_reply_host: raw.ticket_reply_host ?? null,
    sendgrid_inbound_parse_hostname: raw.sendgrid_inbound_parse_hostname ?? null,
  };
}

function emptyMailDomainStatus() {
  return {
    registered: false,
    mail_domain_verified: false,
    mail_auth_domain: null,
    ticket_reply_host: null,
    sendgrid_inbound_parse_hostname: null,
  };
}

export const fetchMailDomainStatus = createAsyncThunk(
  "mailDomainAuth/fetchStatus",
  async (_, { getState, rejectWithValue }) => {
    const businessId = getBusinessId(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.get(
        `${ROOT_URL}/business/${businessId}/mail-domain-status`,
        getAuthConfig(getState),
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return rejectWithValue({ notFound: true, status: 404 });
      }
      return rejectWithValue(
        error.response?.data || { message: error.message },
      );
    }
  },
);

export const fetchMailDomainAuth = createAsyncThunk(
  "mailDomainAuth/fetch",
  async (_, { getState, rejectWithValue }) => {
    const businessId = getBusinessId(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.get(
        `${ROOT_URL}/business/${businessId}/mail-domain-auth`,
        getAuthConfig(getState),
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return rejectWithValue({ notFound: true, status: 404 });
      }
      return rejectWithValue(
        error.response?.data || { message: error.message },
      );
    }
  },
);

export const registerMailDomain = createAsyncThunk(
  "mailDomainAuth/register",
  async ({ domain }, { getState, rejectWithValue }) => {
    const businessId = getBusinessId(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.post(
        `${ROOT_URL}/business/${businessId}/mail-domain-auth`,
        { domain: domain.trim() },
        getAuthConfig(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message },
      );
    }
  },
);

export const validateMailDomain = createAsyncThunk(
  "mailDomainAuth/validate",
  async (_, { getState, rejectWithValue }) => {
    const businessId = getBusinessId(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.post(
        `${ROOT_URL}/business/${businessId}/mail-domain-auth/validate`,
        {},
        getAuthConfig(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message },
      );
    }
  },
);

export const deleteMailDomainAuth = createAsyncThunk(
  "mailDomainAuth/delete",
  async (_, { getState, rejectWithValue }) => {
    const businessId = getBusinessId(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.delete(
        `${ROOT_URL}/business/${businessId}/mail-domain-auth`,
        getAuthConfig(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message },
      );
    }
  },
);

const initialState = {
  data: null,
  statusData: null,
  statusLoading: false,
  statusError: null,
  fetchLoading: false,
  registerLoading: false,
  validateLoading: false,
  deleteLoading: false,
  error: null,
  validateMessage: null,
};

const mailDomainAuthSlice = createSlice({
  name: "mailDomainAuth",
  initialState,
  reducers: {
    clearMailDomainError: (state) => {
      state.error = null;
    },
    clearValidateMessage: (state) => {
      state.validateMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMailDomainStatus.pending, (state) => {
        state.statusLoading = true;
        state.statusError = null;
      })
      .addCase(fetchMailDomainStatus.fulfilled, (state, action) => {
        state.statusLoading = false;
        state.statusError = null;
        const normalized = normalizeMailDomainStatusPayload(
          action.payload?.data,
        );
        state.statusData = normalized ?? emptyMailDomainStatus();
      })
      .addCase(fetchMailDomainStatus.rejected, (state, action) => {
        state.statusLoading = false;
        const p = action.payload;
        state.statusData = emptyMailDomainStatus();
        if (p?.notFound || p?.status === 404) {
          state.statusError = null;
        } else {
          state.statusError = p;
        }
      })
      .addCase(fetchMailDomainAuth.pending, (state) => {
        state.fetchLoading = true;
        state.error = null;
      })
      .addCase(fetchMailDomainAuth.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.data = action.payload?.data ?? null;
        const normalized = normalizeMailDomainStatusPayload(state.data);
        state.statusData = normalized ?? emptyMailDomainStatus();
      })
      .addCase(fetchMailDomainAuth.rejected, (state, action) => {
        state.fetchLoading = false;
        const p = action.payload;
        if (p?.notFound || p?.status === 404) {
          state.data = null;
          state.error = null;
          state.statusData = emptyMailDomainStatus();
        } else {
          state.error = p;
        }
      })
      .addCase(registerMailDomain.pending, (state) => {
        state.registerLoading = true;
        state.error = null;
      })
      .addCase(registerMailDomain.fulfilled, (state, action) => {
        state.registerLoading = false;
        state.data = action.payload?.data ?? state.data;
        const normalized = normalizeMailDomainStatusPayload(state.data);
        if (normalized) state.statusData = normalized;
      })
      .addCase(registerMailDomain.rejected, (state, action) => {
        state.registerLoading = false;
        state.error = action.payload;
      })
      .addCase(validateMailDomain.pending, (state) => {
        state.validateLoading = true;
        state.error = null;
        state.validateMessage = null;
      })
      .addCase(validateMailDomain.fulfilled, (state, action) => {
        state.validateLoading = false;
        state.validateMessage = action.payload?.message ?? null;
      })
      .addCase(validateMailDomain.rejected, (state, action) => {
        state.validateLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteMailDomainAuth.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteMailDomainAuth.fulfilled, (state) => {
        state.deleteLoading = false;
        state.data = null;
        state.validateMessage = null;
        state.statusData = emptyMailDomainStatus();
      })
      .addCase(deleteMailDomainAuth.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearMailDomainError, clearValidateMessage } =
  mailDomainAuthSlice.actions;
export default mailDomainAuthSlice.reducer;
